import type { NewEventPlacement } from '@awwyeah/shared';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HouseholdContent } from '../modules/household';
import { usePushSubscription } from '../push/usePushSubscription';
import { sendPoke } from '../push/api';
import { isStandalone } from '../push/platform';
import { useInstallPrompt } from '../onboarding/useInstallPrompt';
import { IconSetSwitcher, ThemeSwitcher } from '../theme/ThemeSwitcher';
import { CalendarFeedSection } from '../modules/calendar';
import { useNewEventPlacement } from '../modules/calendar/useCalendarData';
import { useWeekStart, type WeekStartPreference } from '../modules/calendar/weekStartPreference';
import { ConnectClaudeSection } from '../modules/mcp';
import { FeatureRequestsSection } from '../modules/feedback';

/**
 * Settings page. As of 2026-07-31 this is also home to a screen that used to have its own
 * nav slot (docs/DECISIONS.md, owner call): the household roster + invite-link generator
 * (`HouseholdContent`, still also reachable at `/household` directly) up top. The push
 * section remains the one place to manually exercise the whole client→server→client push
 * loop during development.
 *
 * The "Developer" section (a link to the sync playground, `/dev/sync`) was removed from
 * this page per a later owner call the same day (docs/DECISIONS.md) — the route and module
 * are unchanged and still power the WS-8 torture suite, it's just no longer linked from
 * Settings; reach it by URL directly.
 */
export function SettingsPage(): JSX.Element {
  const standalone = useMemo(() => isStandalone(), []);
  const push = usePushSubscription();
  const install = useInstallPrompt();

  return (
    <div className="mx-auto max-w-lg space-y-6 py-8 lg:max-w-2xl">
      <header>
        <h1 className="font-display text-2xl tracking-tight text-fg">Settings</h1>
      </header>

      <section className="space-y-3" data-testid="settings-household">
        <h2 className="text-lg font-semibold text-fg">Household</h2>
        <HouseholdContent />
      </section>

      <Link
        to="/apps"
        data-testid="settings-apps-link"
        className="flex min-h-[56px] items-center justify-between rounded-lg border border-line p-4"
      >
        <span>
          <span className="block text-base font-semibold text-fg">Apps &amp; experiments</span>
          <span className="block text-sm text-fg-3">Notes, sketches, trackers</span>
        </span>
        <span aria-hidden="true" className="text-fg-3">
          ›
        </span>
      </Link>

      <section
        className="space-y-3 rounded-lg border border-line p-4"
        data-testid="settings-appearance"
      >
        <h2 className="text-lg font-semibold text-fg">Appearance</h2>
        <p className="text-xs leading-snug text-fg-3">
          Both settings are saved on this device only — everyone in the household can pick their
          own.
        </p>

        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-fg-2">Theme</h3>
          <ThemeSwitcher />
        </div>

        <div className="space-y-1 pt-2">
          <h3 className="text-sm font-semibold text-fg-2">Icons</h3>
          <IconSetSwitcher />
        </div>
      </section>

      <CalendarSection />

      <CalendarFeedSection />

      <ConnectClaudeSection />

      {/* Only renders for the feedback recipient (Dan) — the household's ideas inbox. */}
      <FeatureRequestsSection />

      <section className="space-y-3 rounded-lg border border-line p-4">
        <h2 className="text-lg font-semibold text-fg">App</h2>
        <Row
          label="Install state"
          value={standalone ? 'Installed (standalone)' : 'Running in browser'}
        />
        {!standalone && (
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Link
              to="/install"
              className="inline-flex min-h-[44px] items-center text-sm font-medium text-accent underline"
            >
              Install AwwYeah →
            </Link>
            {install.canPromptNatively && (
              <button
                type="button"
                onClick={() => void install.promptNatively()}
                data-testid="settings-native-install"
                className="min-h-[44px] rounded-xl bg-inverse px-4 py-2 text-sm font-semibold text-inverse-fg"
              >
                Install now
              </button>
            )}
            {install.bannerDismissed && (
              <button
                type="button"
                onClick={install.resetBannerDismissal}
                data-testid="settings-reset-install-banner"
                className="min-h-[44px] rounded-xl border border-line px-4 py-2 text-sm font-semibold text-fg-2"
              >
                Show install banner again
              </button>
            )}
          </div>
        )}
      </section>

      <section className="space-y-3 rounded-lg border border-line p-4">
        <h2 className="text-lg font-semibold text-fg">Notifications</h2>
        <Row label="Browser support" value={push.supported ? 'Supported' : 'Not supported'} />
        <Row label="Permission" value={push.permission} />
        <Row
          label="Server push config"
          value={
            push.serverConfigured === 'loading'
              ? 'Checking…'
              : push.serverConfigured
                ? 'Configured'
                : 'Not configured (missing VAPID keys)'
          }
        />
        <Row label="Subscribed on this device" value={push.subscribed ? 'Yes' : 'No'} />

        <div className="flex flex-wrap gap-2 pt-2">
          {!push.subscribed ? (
            <button
              type="button"
              onClick={() => void push.subscribe()}
              disabled={push.busy || !push.supported}
              className="min-h-[44px] rounded-xl bg-inverse px-4 py-2 font-semibold text-inverse-fg disabled:opacity-60"
            >
              {push.busy ? 'Working…' : 'Enable notifications'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void push.unsubscribe()}
              disabled={push.busy}
              className="min-h-[44px] rounded-xl border border-line px-4 py-2 font-semibold text-fg-2 disabled:opacity-60"
            >
              {push.busy ? 'Working…' : 'Disable notifications'}
            </button>
          )}
        </div>
        {push.error && <p className="text-sm text-danger">{push.error}</p>}

        <PokeSection subscribed={push.subscribed} />
      </section>
    </div>
  );
}

/** Test-your-own-devices button + a short message box to ping everyone else — the
 * owner-requested way to verify pushes end-to-end (and lovingly annoy your spouse). */
function PokeSection({ subscribed }: { subscribed: boolean }): JSX.Element {
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const poke = async (to: 'me' | 'others'): Promise<void> => {
    setBusy(true);
    setStatus(null);
    try {
      const result = await sendPoke(to, message.trim() || undefined);
      setStatus(
        to === 'me'
          ? result.sent > 0
            ? 'Sent — check your notifications.'
            : 'No devices subscribed for you yet.'
          : result.sent > 0
            ? `Sent to ${result.sent} device${result.sent === 1 ? '' : 's'}.`
            : 'Nobody else has notifications on yet.',
      );
      if (to === 'others') setMessage('');
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 border-t border-line pt-3">
      <h3 className="text-sm font-semibold text-fg-2">Test &amp; poke</h3>
      <button
        type="button"
        data-testid="push-self-test"
        onClick={() => void poke('me')}
        disabled={busy || !subscribed}
        className="min-h-[44px] rounded-xl border border-line px-4 py-2 text-sm font-semibold text-fg-2 disabled:opacity-60"
      >
        Send a test to my devices
      </button>
      <div className="flex gap-2">
        <input
          data-testid="poke-message-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && message.trim()) void poke('others');
          }}
          maxLength={120}
          placeholder="Say something to everyone else…"
          className="h-11 min-w-0 flex-1 rounded-xl border border-line bg-bg px-3 text-sm text-fg placeholder:text-fg-3"
        />
        <button
          type="button"
          data-testid="poke-send"
          onClick={() => void poke('others')}
          disabled={busy || !message.trim()}
          className="min-h-[44px] shrink-0 rounded-xl bg-inverse px-4 text-sm font-semibold text-inverse-fg disabled:opacity-60"
        >
          Poke
        </button>
      </div>
      {status && <p className="text-sm text-fg-3">{status}</p>}
    </div>
  );
}

function CalendarSection(): JSX.Element {
  const { preference, setPreference } = useWeekStart();
  const { placement, setPlacement } = useNewEventPlacement();
  const options: { value: WeekStartPreference; label: string }[] = [
    { value: 'sunday', label: 'Sunday' },
    { value: 'monday', label: 'Monday' },
  ];
  const placements: { value: NewEventPlacement; label: string }[] = [
    { value: 'family', label: 'Family' },
    { value: 'private', label: 'Just me' },
  ];
  return (
    <section
      className="space-y-3 rounded-lg border border-line p-4"
      data-testid="settings-calendar"
    >
      <h2 className="text-lg font-semibold text-fg">Calendar</h2>
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-fg-3">Weeks start on</span>
        <div className="flex gap-2">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              data-testid={`week-start-${option.value}`}
              onClick={() => setPreference(option.value)}
              className={`min-h-[44px] rounded-md px-4 text-sm font-semibold ${
                preference === option.value
                  ? 'bg-inverse text-inverse-fg'
                  : 'border border-line text-fg-2'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-fg-3">My new events start as</span>
        <div className="flex gap-2">
          {placements.map((option) => (
            <button
              key={option.value}
              type="button"
              data-testid={`new-events-${option.value}`}
              aria-pressed={placement === option.value}
              onClick={() => setPlacement(option.value)}
              className={`min-h-[44px] rounded-md px-4 text-sm font-semibold ${
                placement === option.value
                  ? 'bg-inverse text-inverse-fg'
                  : 'border border-line text-fg-2'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs leading-snug text-fg-3">
        {placement === 'private'
          ? 'Events you add are yours alone unless you choose to share one. This follows you to every device.'
          : "Events you add go on everyone's calendar unless you pick “Just me”. This follows you to every device."}
      </p>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-fg-3">{label}</span>
      <span className="font-medium text-fg">{value}</span>
    </div>
  );
}
